/**
 * Sticky session reply language — stick until asked (or sustained visitor change).
 * Returns BCP-47 codes (en-US, nb-NO, fa-IR) — not UI locale codes.
 *
 * Product contract:
 * 1) Stick to the language the visitor is currently speaking/writing.
 * 2) On explicit request → switch cleanly and stay.
 * 3) Do NOT flip mid-turn / mid-reply from mixed ASR or weak heuristics.
 */

export type LanguageStreak = { code: string; count: number }

export type StickyLanguageResult = {
  languageCode: string
  streak: LanguageStreak
  reason: 'explicit_switch' | 'sustained' | 'stick' | 'establish'
}

/** Consecutive strong detections required before an unbidden language change. */
export const SUSTAINED_LANGUAGE_SWITCH_THRESHOLD = 2

function normalizeLang(code: string): string {
  const lower = code.trim().toLowerCase()
  if (lower.startsWith('nb') || lower.startsWith('no')) return 'nb-NO'
  if (lower.startsWith('fa') || lower.startsWith('per')) return 'fa-IR'
  if (lower.startsWith('en')) return 'en-US'
  return code
}

function langsEqual(a: string, b: string): boolean {
  return normalizeLang(a) === normalizeLang(b)
}

/** Explicit visitor request to switch reply language. */
export function detectExplicitLanguageSwitch(text: string): string | null {
  const t = text.trim()
  if (!t) return null
  const lower = t.toLowerCase()

  // Persian / Farsi
  if (
    /\b(speak|talk|reply|respond|answer|switch(?:\s+to)?|use)\b.{0,24}\b(persian|farsi)\b/i.test(t) ||
    /\b(persian|farsi)\s+please\b/i.test(lower) ||
    /\b(به\s*فارسی|فارسی\s*حرف|speak\s+فارسی)\b/i.test(t) ||
    /\bcan we (speak|talk|switch to) (persian|farsi)\b/i.test(lower)
  ) {
    return 'fa-IR'
  }

  // Norwegian
  if (
    /\b(speak|talk|reply|respond|answer|switch(?:\s+to)?|use)\b.{0,24}\b(norwegian|norsk|bokm[aå]l|nynorsk)\b/i.test(
      t,
    ) ||
    /\b(norsk|norwegian)\s+please\b/i.test(lower) ||
    /\b(snakk|svar)\s+(på\s+)?norsk\b/i.test(lower) ||
    /\bcan we (speak|talk|switch to) (norwegian|norsk)\b/i.test(lower) ||
    /^(norsk|norwegian)[.!?]?$/i.test(t.trim())
  ) {
    return 'nb-NO'
  }

  // English
  if (
    /\b(speak|talk|reply|respond|answer|switch(?:\s+to)?|use)\b.{0,24}\benglish\b/i.test(t) ||
    /\benglish\s+please\b/i.test(lower) ||
    /\bin english\b/i.test(lower) ||
    /\bback to english\b/i.test(lower) ||
    /\b(switch|back|reply|answer)\b.{0,20}\benglish\b/i.test(lower) ||
    /\bcan we (speak|talk|switch to) english\b/i.test(lower)
  ) {
    return 'en-US'
  }

  return null
}

/**
 * Strong dominant-language signal only — ignores weak/mixed ASR fragments.
 * Returns null when confidence is too low to justify any change.
 */
export function detectStrongVisitorLanguage(text: string): string | null {
  const trimmed = text.trim()
  if (trimmed.length < 8) return null

  // Arabic / Persian script → Farsi for this product
  const arabicChars = (trimmed.match(/[\u0600-\u06FF]/g) || []).length
  if (arabicChars >= 6) return 'fa-IR'

  const normalized = trimmed.toLowerCase()

  const nbMarkers =
    /\b(hei|hallo|takk|hvordan|hvorfor|hvilken|hva|hvor|nå|også|veldig|bra|dag|uke|år|godt|gjerne|kan|vil|skal|dette|denne|dere|oss|meg|deg|ikke|eller|men|fordi|siden|må|trenger|prosjekt|bedrift|firma)\b/gi
  const enMarkers =
    /\b(the|and|is|are|you|that|have|for|not|with|this|but|from|they|we|what|about|would|could|should|please|thanks|hello|want|need|project|company|business|looking|help)\b/gi

  const nbCount = (normalized.match(nbMarkers) || []).length
  const enCount = (normalized.match(enMarkers) || []).length

  // Require a clear majority and enough markers — blocks single-token ASR flips
  if (nbCount >= 3 && nbCount >= enCount + 2) return 'nb-NO'
  if (enCount >= 3 && enCount >= nbCount + 2) return 'en-US'

  return null
}

/**
 * First typed line, including a short Norwegian sentence that is too thin
 * for the sustained-switch detector. «Vi vil ha en workshop» is nb.
 * Returns null when the line is mixed or too short to call.
 */
export function languageFromFirstTypedMessage(text: string): 'nb-NO' | 'nn-NO' | 'en-US' | null {
  const trimmed = text.trim()
  if (trimmed.length < 8) return null
  const normalized = trimmed.toLowerCase()
  const nnMarkers =
    /\b(ikkje|eg|kva|korleis|dei|noko|berre|ønskjer|onskjer|dykk|nokon|innehalde|heiter)\b/gi
  const nbMarkers =
    /\b(vi|vil|ha|en|et|jeg|dere|skal|kan|hei|takk|ikke|også|og|som|med|bedrift|firma|oppgave|tilbud|tilbudene|innboks|innboksen|liggende|blir|uke|uken|uker|oppfølging)\b/gi
  const enMarkers =
    /\b(the|and|we|want|would|please|hello|thanks|you|our|with|company|task|quote|quotes|inbox|week|weeks|before|follow)\b/gi
  const nnCount = (normalized.match(nnMarkers) || []).length
  const nbCount = (normalized.match(nbMarkers) || []).length
  const enCount = (normalized.match(enMarkers) || []).length
  // «får», «første», «dagen» are Norwegian even when the dictionary hits are thin.
  if (/[æøå]/i.test(trimmed) && enCount < 2) return nnCount >= 2 ? 'nn-NO' : 'nb-NO'
  if (nnCount >= 2 && enCount === 0) return 'nn-NO'
  if (nbCount >= 2 && enCount === 0 && nnCount === 0) return 'nb-NO'
  if (enCount >= 2 && nbCount === 0 && nnCount === 0) return 'en-US'
  return null
}

/**
 * Resolve sticky reply language for this user turn.
 * - Explicit switch → change immediately
 * - Strong detection matching sticky → stay
 * - Strong detection differing → only switch after sustained streak
 * - Weak / mixed → always stick
 */
export function resolveStickyReplyLanguage(
  text: string,
  stickyLanguageCode: string,
  streak: LanguageStreak = { code: stickyLanguageCode, count: 0 },
): StickyLanguageResult {
  const sticky = normalizeLang(stickyLanguageCode) || 'en-US'

  const explicit = detectExplicitLanguageSwitch(text)
  if (explicit) {
    return {
      languageCode: explicit,
      streak: { code: explicit, count: 0 },
      reason: 'explicit_switch',
    }
  }

  const detected = detectStrongVisitorLanguage(text)
  if (!detected) {
    return { languageCode: sticky, streak, reason: 'stick' }
  }

  if (langsEqual(detected, sticky)) {
    return {
      languageCode: sticky,
      streak: { code: sticky, count: 0 },
      reason: 'stick',
    }
  }

  const nextStreak: LanguageStreak = langsEqual(detected, streak.code)
    ? { code: normalizeLang(detected), count: streak.count + 1 }
    : { code: normalizeLang(detected), count: 1 }

  if (nextStreak.count >= SUSTAINED_LANGUAGE_SWITCH_THRESHOLD) {
    return {
      languageCode: nextStreak.code,
      streak: { code: nextStreak.code, count: 0 },
      reason: 'sustained',
    }
  }

  return {
    languageCode: sticky,
    streak: nextStreak,
    reason: 'stick',
  }
}

/**
 * Back-compat helper used by composers/tests.
 * Prefer resolveStickyReplyLanguage with a streak ref for sustained switches.
 * Without streak state this only honors explicit switches (no mid-reply thrashing).
 */
export function inferLanguagePreference(text: string, fallback: string): string {
  return resolveStickyReplyLanguage(text, fallback, {
    code: fallback,
    count: 0,
  }).languageCode
}
